import { make, segment } from '@mobi/libraries/analytics'
import storage from 'utils/storage'
import { formIdRegister } from 'utils/make/constants'
import generateUUID from 'utils/generateUUID'

export const generateAndGetCorrelationId = () => {
  const storagedCorrelatoinId = storage.session.getItem('correlationId')

  if (!storagedCorrelatoinId) {
    const newCorrelationId = generateUUID()
    storage.session.setItem('correlationId', newCorrelationId)

    return newCorrelationId
  }

  return storagedCorrelatoinId
}

const makeTrackings = {
  pageView: (pageName) => {
    const payload = {
      pageName,
    }

    make.track('redventures.usertracking.v2.PageViewed', payload)
    segment.track('page_view', payload)
  },

  elementClicked: ({
    location = '',
    position = '',
    elementType = '',
    text = '',
    htmlId = '',
    actionOutcome = '',
    outboundUrl = '',
    useCorrelationId = false,
  }) => {
    const correlationId = useCorrelationId ? generateAndGetCorrelationId() : ''
    const payload = {
      webElement: {
        location,
        position,
        elementType,
        text,
        htmlId,
      },
      actionOutcome,
      outboundUrl,
      correlationId,
    }

    make.track('redventures.usertracking.v3.ElementClicked', payload)
    segment.track('button_click', payload)
  },

  formViewed: ({ formName = 'debt_negotiation', formType = 'lead' }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
    }

    make.track('redventures.usertracking.v3.FormViewed', payload)
    segment.track('form_viewed', payload)
  },

  formStarted: ({ formName = 'debt_negotiation', formType = 'lead' } = {}) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
    }

    make.track('redventures.usertracking.v3.FormStarted', payload)
    segment.track('form_started', payload)
  },

  fieldInputted: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    fieldLabel = '',
    fieldId = '',
    fieldName = '',
    fieldType = '',
    fieldValue = 'pii filtered',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      userInputField: {
        fieldLabel,
        fieldId: fieldId?.toString(),
        fieldName,
        fieldType,
        fieldValue,
      },
    }

    make.track('redventures.usertracking.v3.FieldInputted', payload)
    segment.track('field_inputted', payload)
  },

  formContinued: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    stepName = '',
    stepType = '',
    field = [],
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      stepContext: {
        stepName,
        stepType,
      },
      field,
    }

    make.track('redventures.usertracking.v3.FormContinued', payload)
    segment.track('form_continued', payload)
  },

  formBacked: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    stepName = '',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      stepContext: {
        stepName,
      },
    }

    make.track('redventures.usertracking.v3.FormBacked', payload)
    segment.track('form_backed', payload)
  },

  fieldSelected: ({
    fieldType = 'UNKNOWN',
    fieldName = '',
    fieldId = '',
    fieldValue = '',
    fieldLabel = '',
    formName = 'debt_negotiation',
    formType = 'lead',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      userInputField: {
        fieldType,
        fieldName,
        fieldId: fieldId?.toString(),
        fieldValue,
        fieldLabel,
      },
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
    }

    make.track('redventures.usertracking.v3.FieldSelected', payload)
    segment.track('field_selected', payload)
  },

  formSubmitted: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    field = [],
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      field,
    }

    make.track('redventures.usertracking.v3.FormSubmitted', payload)
    segment.track('form_submitted', payload)
  },

  productViewed: ({
    productId = '',
    brand = '',
    location = '',
    category = '',
    position = '',
    price = '',
    text = '',
  }) => {
    const payload = {
      product: {
        productId: productId?.toString(),
        brand,
        category,
        location,
        position,
        price,
        text,
      },
    }

    make.track('redventures.ecommerce.v1.ProductViewed', payload)
    segment.track('product_viewed', payload)
  },

  productClicked: ({
    productId = '',
    brand = '',
    category = '',
    location = '',
    position = '',
    price = '',
    text = '',
    variant = '',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      product: {
        productId: productId?.toString(),
        brand,
        category,
        location,
        position,
        price,
        text,
        variant,
      },
    }

    make.track('redventures.ecommerce.v1.ProductClicked', payload)
    segment.track('product_clicked', payload)
  },

  productAdded: ({
    productId = '',
    brand = '',
    category = '',
    location = '',
    position = null,
    price = null,
    text = '',
    variant = '',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      product: {
        productId: productId?.toString(),
        brand,
        category,
        location,
        position,
        price,
        text,
        variant,
      },
    }

    make.track('redventures.ecommerce.v1.ProductAdded', payload)
    segment.track('product_added', payload)
  },

  elementViewed: ({
    location = '',
    position = '',
    elementType = '',
    text = '',
    htmlId = '',
    useCorrelationId = false,
  }) => {
    const correlationId = useCorrelationId ? generateAndGetCorrelationId() : ''
    const payload = {
      webElement: {
        location,
        position,
        elementType,
        text,
        htmlId,
      },
      correlationId,
    }

    make.track('redventures.usertracking.v3.ElementViewed', payload)
    segment.track('element_viewed', payload)
  },
}

export default makeTrackings
